import { PortableText } from '@portabletext/react'
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next'
import { useLiveQuery } from '~/lib/sanity-live-query'
import { getGlobalData } from '../../utils/global-data';
import Footer from '../../components/Footer';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import StickyNavbar from '../../components/StickyNavbar';
import HorizontalLine from '../../components/HorizontalLine';
import SliderCategory from '../../components/Sliders/SliderCategory';
import CarouselTitle from "~/components/CarouselTitle/CarouselTitle";
import ModuleList from '../../components/ModuleList';

import { readToken } from '~/lib/sanity.api'
import { getClient } from '~/lib/sanity.client'
import {
  getSystem,
  getSystems,
  getSiteConfig,
  systemBySlugQuery,  
  systemsQuery,
  siteConfigQuery,
  getTimbers,   
  timbersQuery,
} from '~/lib/sanity.queries'
import { type System, SiteConfig, SystemTimber, Timber } from '~/lib/sanity.interfaces'
import type { SharedPageProps } from '~/pages/_app'
import SystemAccordion from '~/components/SystemAccordion';
import Link from 'next/link';
import SliderTimber from '~/components/Sliders/SliderTimber';

  interface Query {
    [key: string]: string
  }

  
  
  export const getServerSideProps: GetServerSideProps<
    SharedPageProps & {
      system: System,
      systems: System[],
      timbers: Timber[],
      systemTimber: SystemTimber,
      siteConfig: SiteConfig,
      isChild: boolean,
    },
    Query
  > = async ({ draftMode = false, params = {} }) => {
    const client = getClient(draftMode ? { token: readToken } : undefined)
    let isChild = false  

    const system = await getSystem(client, params.slug[0])
    const systems = await getSystems(client)
    const timbers = await getTimbers(client)
    const siteConfig = await getSiteConfig(client)
    const globalData = getGlobalData()
  
    if (!system) {
      return {
        notFound: true,
      }
    }

    let systemTimber = null

    const selectTimberBySlug = (slug) => {
      return system.selectedSystemTimbers.find(timber => timber.slug.current === slug);
    };

    if(params.slug.length > 1){
      systemTimber = selectTimberBySlug(params.slug[1])
      isChild = true;
    }
  
    return {
      props: {
        globalData,
        draftMode,
        token: draftMode ? readToken : '',
        system,
        systems,
        timbers,
        systemTimber,
        siteConfig,
        isChild,
      },
    }
  }

export default function InspirationPage(
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
  globalData
) {
  const [system] = useLiveQuery(props.draftMode, props.system, systemBySlugQuery, {
    slug: props.system.slug.current,
  })
  const [systems] = useLiveQuery<System[]>(props.draftMode, props.systems, systemsQuery)
  const [timbers] = useLiveQuery<Timber[]>(props.draftMode, props.timbers, timbersQuery)
  const [siteConfig] = useLiveQuery(props.draftMode, props.siteConfig, siteConfigQuery)
  const systemTimber = props.systemTimber
  const isChild = props.isChild

  const slides = []
  if(system.mainCarousel) {
    system.mainCarousel.map((item) => {
      slides.push({
        _id: item._key,
        image: item,
        title: item.title
      });
    })
  }

  return (
    <Layout>
        <SEO title={system.seoTitle} description={system.seoDescription} keywords={system.seoKeywords} />
        <StickyNavbar siteConfig={siteConfig} />
        <main className="w-full bg-background nav-offset py-4 lg:py-8">    
            
            {system.systemType === 'system' && !isChild &&(   
              <>
              <div className='mx-2 md:mx-10 mb-12'>
                <div className='grid grid-cols-1 lg:grid-cols-2'>
                  <div className='border border-black'>                                     
                    <div className='p-10'>
                      <h1 className="text-3xl lg:text-5xl text-left mb-12 mr-10">
                        {system.title}
                      </h1>  
                      <div className='contentBlock text-left text-lg'>
                        <PortableText value={system.body} />
                      </div>                      
                    </div>
                  </div>
                  <div className=''>
                    <CarouselTitle slides={slides} />
                  </div>
                </div>
              </div> 
              {system.pageModules && (
              <div className='mx-2 md:mx-10'>
                <ModuleList moduleList={system.pageModules} />
              </div> 
              )}  
              {system.selectedSystemTimbers && (
                <div className='mx-2 md:mx-10'>
                  <div className="text-left">
                      <h3 className="text-4xl font-normal text-primary mb-10">Select for appropriate profiles and details by timber species</h3>
                  </div> 
                  <SliderCategory items={system.selectedSystemTimbers} prefix={system.slug.current + '/'} />           
                </div> 
              )}               
              </>
            )} 
            {system.systemType === 'system' && isChild &&(              
              <>            
              <div className=''>
                <SystemAccordion sections={systemTimber.sections} />
                <HorizontalLine />
              </div>
              <div className='mx-2 md:mx-10'>
                {system.selectedSystemTimbers && ( 
                  <>
                    <div className="text-right">
                        <h3 className="text-4xl font-semibold text-primary mb-10">Available Timbers</h3>
                    </div>                 
                    <SliderCategory items={system.selectedSystemTimbers} prefix={'/systems/' + system.slug.current + '/'} />                      
                    <HorizontalLine />
                  </>
                )}  
                {system.selectedSystems && ( 
                  <>
                    <div className="text-right">
                        <h3 className="text-4xl font-semibold text-primary mb-10">Timber Systems</h3>
                    </div>                 
                    <SliderCategory items={system.selectedSystems} prefix="/systems/" />     
                  </>
                )}                 
              </div>  
            </>
            )} 
            {system.systemType === 'simple' && (
              <>  
              <div className='mx-2 md:mx-10 mb-12'>
                <div className='grid grid-cols-1 lg:grid-cols-2'>
                  <div className='border border-black'>                                     
                    <div className='p-10'>
                      <h1 className="text-3xl lg:text-5xl text-left mb-12 mr-10">
                        {system.title}
                      </h1>  
                      <div className='contentBlock text-left text-lg'>
                        <PortableText value={system.body} />
                      </div>                      
                    </div>
                  </div>
                  <div className=''>
                    <CarouselTitle slides={slides} />
                  </div>
                </div>
              </div>
              {system.pageModules && (
              <div className='mx-2 md:mx-10'>
                <ModuleList moduleList={system.pageModules} />
              </div> 
              )}   
              <div className='mx-2 md:mx-10'>
                {system.selectedTimbers && ( 
                  <>
                    <div className="text-right">
                        <h3 className="text-4xl font-semibold text-primary mb-10">Available Timbers</h3>
                    </div>                 
                    <SliderTimber timbers={system.selectedTimbers} />
                    <div className='flex justify-end'>
                      <Link className="mt-2 font-bold underline" href="/timbers">See all Timber Species</Link>
                    </div>
                    <HorizontalLine />
                  </>
                )}  
                {system.selectedSystems && ( 
                  <>
                    <div className="text-right">
                        <h3 className="text-4xl font-semibold text-primary mb-10">Timber Systems</h3>
                    </div>                 
                    <SliderCategory items={system.selectedSystems} prefix="/systems/" />     
                  </>
                )}                 
              </div>
            </>
            )}   
            {system.systemType === 'product' && (
              <>   
              <div className='mx-2 md:mx-10 mb-12'>
                <div className='grid grid-cols-1 lg:grid-cols-2'>
                  <div className='border border-black'>                                     
                    <div className='p-10'>
                      <h1 className="text-3xl lg:text-5xl text-left mb-12 mr-10">
                        {system.title}
                      </h1>  
                      <div className='contentBlock text-left text-lg'>
                        <PortableText value={system.body} />
                      </div>                      
                    </div>
                  </div>
                  <div className=''>
                    <CarouselTitle slides={slides} />
                  </div>
                </div>
              </div> 
              {system.pageModules && (
              <div className='mx-2 md:mx-10'>
                <ModuleList moduleList={system.pageModules} />
              </div> 
              )}
              <div className='mx-2 md:mx-10'>
                {system.selectedTimbers && ( 
                  <>
                    <div className="text-right">
                        <h3 className="text-4xl font-semibold text-primary mb-10">Available Timbers</h3>
                    </div>                 
                    <SliderTimber timbers={system.selectedTimbers} />
                    <div className='flex justify-end'>
                      <Link className="mt-2 font-bold underline" href="/timbers">See all Timber Species</Link>
                    </div>
                    <HorizontalLine />
                  </>
                )}  
                {system.selectedSystems && ( 
                  <>
                    <div className="text-right">
                        <h3 className="text-4xl font-semibold text-primary mb-10">Other Products</h3>
                    </div>                 
                    <SliderCategory items={system.selectedSystems} prefix="/systems/" />     
                  </>
                )}                 
              </div>
            </>
            )}               
            </main>        
        <Footer siteConfig={siteConfig} />      
    </Layout>
  );
}


